/**
 * AdminUsersManagement Component
 * 
 * Handles all user management functionality:
 * - User listing with search and filtering
 * - Create new users
 * - User actions (suspend, reactivate, delete)
 * - Tier management
 * - Password reset
 * - User activity viewing
 * - VIP circle sync
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, 
  Search, 
  Plus, 
  Trash2, 
  Eye,
  Mail,
  Phone,
  Calendar,
  Clock,
  Shield,
  MessageSquare,
  BookOpen,
  RefreshCw,
  Flag,
  Lock,
  Heart,
  CheckCircle2,
  XCircle,
  Loader2,
  Bell,
  Diamond,
  Send,
  UserPlus,
  Smartphone,
  Activity,
  ArrowUpDown,
  X,
  CalendarDays,
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  Crown,
  MessageCircle,
  MessageSquarePlus,
  Inbox,
  Star,
  LogIn,
  ClipboardCheck,
  CalendarX,
  CreditCard,
  ShieldOff,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Progress } from '@/components/ui/progress';

const API_URL = window.location.origin;

const TIER_OPTIONS = [
  { value: 'inner_circle', label: 'Inner Circle' },
  { value: 'inner_edge', label: 'Inner Edge' },
  { value: 'coaching_activation', label: 'Coaching Activation' },
  { value: 'sdc', label: 'SDC' },
  { value: 'costa_rica', label: 'Costa Rica Retreat' },
  { value: 'pathways', label: 'Pathways' },
  { value: 'legacy_circle', label: 'Legacy Circle' }
];

const AdminUsersManagement = () => {
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  // Access status filter — 'active' (default; hides cancelled/terminated/deleted),
  // 'all' (no filter), or a specific status like 'cancelled_nonpayment'.
  // Critical for finding disputing customers who got auto-cancelled after 3
  // failed payments and otherwise look "deleted".
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  // Batch selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBatchTierDialog, setShowBatchTierDialog] = useState(false);
  const [batchTierAction, setBatchTierAction] = useState('add'); // 'add' or 'remove'
  const [batchTierValue, setBatchTierValue] = useState('');
  const [batchProcessing, setBatchProcessing] = useState(false);
  
  // Batch Email state
  const [showBatchEmailDialog, setShowBatchEmailDialog] = useState(false);
  const [batchEmailSubject, setBatchEmailSubject] = useState('');
  const [batchEmailMessage, setBatchEmailMessage] = useState('');
  const [sendingBatchEmail, setSendingBatchEmail] = useState(false);
  
  // Batch Status state
  const [showBatchStatusDialog, setShowBatchStatusDialog] = useState(false);
  const [batchStatusAction, setBatchStatusAction] = useState('suspend'); // 'suspend', 'reactivate', 'delete'
  
  // Dialog states
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showUserActionsDialog, setShowUserActionsDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  
  // Session Wallet state
  const [walletType, setWalletType] = useState('');
  const [walletAmount, setWalletAmount] = useState(0);
  
  // Form states
  const [createUserForm, setCreateUserForm] = useState({
    email: '', 
    full_name: '', 
    password: '', 
    phone_number: '',
    role: 'client', 
    active_tiers: [], 
    send_welcome_email: true
  });
  const [suspendReason, setSuspendReason] = useState('');

  // Edit profile state (admin can edit name + email for any user)
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Activity states
  const [userActivity, setUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Sync state
  const [syncingVipCircles, setSyncingVipCircles] = useState(false);

  // SDC Payment Link state
  const [sendingSdcLink, setSendingSdcLink] = useState(false);

  // Send a /sdc-payment/:token invitation to this user — generates (or reuses) a token,
  // optionally emails the user, and copies the URL to clipboard.
  const handleSaveProfile = async () => {
    if (!selectedUserForAction) return;
    const trimmedName = (editName || '').trim();
    const trimmedEmail = (editEmail || '').trim().toLowerCase();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!trimmedEmail || !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const nameChanged = trimmedName !== (selectedUserForAction.full_name || '');
    const emailChanged = trimmedEmail !== (selectedUserForAction.email || '').toLowerCase();
    if (!nameChanged && !emailChanged) {
      toast.info('No changes to save');
      return;
    }
    if (emailChanged) {
      const ok = window.confirm(
        `Change email from "${selectedUserForAction.email}" to "${trimmedEmail}"?\n\n` +
        `IMPORTANT: this is a manual change — make sure the new email is already updated in Ontraport too, otherwise webhooks may create a duplicate account.`
      );
      if (!ok) return;
    }
    setSavingProfile(true);
    try {
      const payload = {};
      if (nameChanged) payload.full_name = trimmedName;
      if (emailChanged) payload.email = trimmedEmail;
      payload.notes = 'Admin edit: profile fields updated via Users panel';
      await axios.put(`${API_URL}/api/admin/users/${selectedUserForAction.id}`, payload);
      toast.success('Profile updated');
      // Reflect locally so dialog shows latest
      setSelectedUserForAction({ ...selectedUserForAction, full_name: trimmedName, email: trimmedEmail });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Failed to update profile';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length) msg = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
      toast.error(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSendSdcLink = async (user) => {
    if (!user?.email || !user?.full_name) {
      alert('User is missing name or email');
      return;
    }
    const sendEmail = window.confirm(
      `Send Sister Diamonds Collective payment link to ${user.full_name} (${user.email})?\n\nClick OK to also email them the link.\nClick Cancel to just generate the link without emailing.`
    );
    setSendingSdcLink(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/sdc-invitations`,
        {
          applicant_email: user.email,
          applicant_name: user.full_name,
          send_email: sendEmail,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res?.data?.invitation_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          // clipboard may be blocked — prompt will show link below
        }
        window.prompt('SDC payment link (copy and share):', url);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create payment invitation');
    } finally {
      setSendingSdcLink(false);
    }
  };

  // Bulk Import states
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportParsing, setBulkImportParsing] = useState(false);
  const [bulkImportProgress, setBulkImportProgress] = useState(0);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [bulkImportSendEmails, setBulkImportSendEmails] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, tierFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/users?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (tierFilter) url += `tier=${tierFilter}&`;
      // Status filter wiring:
      //   'active' → server default (excludes cancelled/terminated/deleted)
      //   'all'    → include everything
      //   specific status string → only that status
      if (statusFilter === 'all') {
        url += `include_inactive=true&`;
      } else if (statusFilter && statusFilter !== 'active') {
        url += `access_status=${encodeURIComponent(statusFilter)}&include_inactive=true&`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.email || !createUserForm.full_name) {
      toast.error('Email and name are required');
      return;
    }
    if (!createUserForm.active_tiers || createUserForm.active_tiers.length === 0) {
      toast.error('Please select at least one membership level');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/create`, createUserForm);
      const successMsg = response.data.temp_password 
        ? `Member created! Temp password: ${response.data.temp_password}` 
        : 'Member created successfully!';
      const emailMsg = response.data.email_sent ? ' Welcome email sent.' : '';
      toast.success(successMsg + emailMsg);
      setShowCreateUserDialog(false);
      setCreateUserForm({ 
        email: '', 
        full_name: '', 
        password: '', 
        phone_number: '', 
        role: 'client', 
        active_tiers: [], 
        send_welcome_email: true 
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create member');
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/suspend?reason=${encodeURIComponent(suspendReason)}`);
      toast.success('User suspended');
      setShowUserActionsDialog(false);
      setSuspendReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/reactivate`);
      toast.success('User reactivated');
      setShowUserActionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleWalletAdjust = async () => {
    if (!selectedUserForAction || !walletType || !walletAmount) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUserForAction.id}/adjust-wallet`,
        { type: walletType, amount: walletAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Adjusted ${walletType.replace(/_/g, ' ')}: ${walletAmount > 0 ? '+' : ''}${walletAmount}`);
      const walletField = `${walletType}_remaining`;
      setSelectedUserForAction(prev => prev ? {
        ...prev,
        [walletField]: Math.max(0, (prev[walletField] || 0) + Number(walletAmount))
      } : prev);
      setWalletType('');
      setWalletAmount(0);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust wallet');
    }
  };


  const handleDeleteUser = async (userId, permanent = false) => {
    const confirmMsg = permanent 
      ? 'This will PERMANENTLY delete this user and ALL their data. This cannot be undone. Are you sure?'
      : 'This will soft-delete the user (they can be restored later). Continue?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}?permanent=${permanent}`);
      toast.success(permanent ? 'User permanently deleted' : 'User terminated');
      setShowUserActionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('This will reset the user\'s password and send them a new one via email. Continue?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`);
      toast.success(`Password reset! New password: ${response.data.new_password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleSMSPasswordReset = async (userId) => {
    if (!window.confirm('This will send a password reset code via SMS to the user\'s phone. Continue?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/send-password-reset-sms`, { user_id: userId });
      toast.success(response.data.message || 'Password reset code sent via SMS!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS reset code');
    }
  };

  const handleAdminFlagUser = async (userId, userName) => {
    const reason = window.prompt(`Why are you flagging ${userName}? (Enter reason)`);
    if (!reason) return;
    
    try {
      await axios.post(`${API_URL}/api/admin/safety/flag-user`, {
        user_id: userId,
        reason: reason,
        flagged_by_admin: true
      });
      toast.success(`${userName} has been flagged for review`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to flag user');
    }
  };

  const handleRestrictDMs = async (userId, restrict) => {
    const action = restrict ? 'restrict' : 'restore';
    if (!window.confirm(`This will ${action} DM access for this user. Continue?`)) return;
    
    try {
      await axios.post(`${API_URL}/api/admin/safety/restrict-dms`, {
        user_id: userId,
        restrict: restrict
      });
      toast.success(`DM access ${restrict ? 'restricted' : 'restored'}`);
      setShowUserActionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} DMs`);
    }
  };

  const handleResendWelcomeEmail = async (userId, emailType = 'general') => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/resend-welcome`, {
        user_id: userId,
        email_type: emailType
      });
      toast.success(response.data.message || 'Welcome email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send welcome email');
    }
  };

  const handleSyncVipCircles = async () => {
    if (!window.confirm('This will sync all users with VIP-eligible tiers (Inner Edge, Costa Rica, SDC) to their respective VIP circles. Continue?')) return;
    
    setSyncingVipCircles(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/sync-vip-circles`);
      toast.success(response.data.message || `Synced ${response.data.memberships_synced} VIP circle memberships`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync VIP circles');
    } finally {
      setSyncingVipCircles(false);
    }
  };

  const fetchUserActivity = async (userId) => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/activity`);
      setUserActivity(response.data);
      setShowActivityDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch user activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  /**
   * Download a ZIP containing every record useful for defending a Stripe
   * dispute or chargeback for one user. Uses fetch (not axios) because we
   * need to handle a binary Blob response with auth headers — anchor tags
   * don't support custom headers.
   */
  const handleDownloadChargebackPack = async (u) => {
    if (!u?.id) return;
    const token = localStorage.getItem('token');
    const t = toast.loading('Building chargeback pack…');
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${u.id}/chargeback-pack`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeEmail = (u.email || 'user').replace(/[^a-z0-9]/gi, '_');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `chargeback-pack-${safeEmail}-${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Chargeback pack downloaded', { id: t });
    } catch (err) {
      console.error('Chargeback pack download failed:', err);
      toast.error(`Failed: ${err.message}`, { id: t });
    }
  };



  // Bulk Import Functions
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return []; // Need header + at least one row
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    const users = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));
      if (values.length < 2) continue; // Need at least email and name
      
      const user = {};
      headers.forEach((header, idx) => {
        user[header] = values[idx] || '';
      });
      
      // Map common header variations
      const email = user.email || user['e-mail'] || user['email address'] || '';
      const name = user.name || user.full_name || user['full name'] || user['first name'] || '';
      const phone = user.phone || user.phone_number || user['phone number'] || user.mobile || '';
      const tiers = user.tier || user.tiers || user.membership || user['membership tier'] || '';
      
      if (email && name) {
        users.push({
          email: email.trim(),
          full_name: name.trim(),
          phone_number: phone.trim(),
          tiers: tiers ? tiers.split(/[;|]/).map(t => t.trim()).filter(Boolean) : []
        });
      }
    }
    
    return users;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setBulkImportParsing(true);
    setBulkImportFile(file);
    setBulkImportResults(null);
    
    try {
      const text = await file.text();
      const users = parseCSV(text);
      
      if (users.length === 0) {
        toast.error('No valid users found in CSV. Make sure it has "email" and "name" columns.');
        setBulkImportFile(null);
      } else {
        setBulkImportData(users);
        toast.success(`Found ${users.length} users in CSV`);
      }
    } catch (error) {
      toast.error('Failed to parse CSV file');
      setBulkImportFile(null);
    } finally {
      setBulkImportParsing(false);
    }
  };

  const handleBulkImport = async () => {
    if (bulkImportData.length === 0) {
      toast.error('No users to import');
      return;
    }
    
    setImporting(true);
    setBulkImportProgress(0);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/bulk-import`, {
        users: bulkImportData,
        send_welcome_emails: bulkImportSendEmails
      });
      
      setBulkImportResults(response.data);
      setBulkImportProgress(100);
      
      if (response.data.created > 0) {
        toast.success(`Successfully created ${response.data.created} users`);
        fetchUsers(); // Refresh user list
      }
      
      if (response.data.skipped > 0) {
        toast.warning(`${response.data.skipped} users were skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setImporting(false);
    }
  };

  const resetBulkImport = () => {
    setBulkImportFile(null);
    setBulkImportData([]);
    setBulkImportResults(null);
    setBulkImportProgress(0);
  };

  const downloadCSVTemplate = () => {
    const template = 'email,name,phone,tier\njohn@example.com,John Doe,+1234567890,inner_circle\njane@example.com,Jane Smith,,inner_edge';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Batch Selection Functions
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === sortedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(sortedUsers.map(u => u.id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const getSelectedUsersList = () => {
    return users.filter(u => selectedUsers.has(u.id));
  };

  // Batch Actions
  const handleBatchExport = () => {
    const selectedUsersList = getSelectedUsersList();
    if (selectedUsersList.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const headers = ['name', 'email', 'phone', 'tiers', 'joined', 'status'];
    const rows = selectedUsersList.map(u => [
      u.full_name || '',
      u.email || '',
      u.phone_number || '',
      (u.active_tiers || []).join(';'),
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
      u.status || 'active'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${selectedUsersList.length} users`);
  };

  const handleBatchTierChange = async () => {
    if (!batchTierValue) {
      toast.error('Please select a tier');
      return;
    }
    
    const selectedUsersList = getSelectedUsersList();
    if (selectedUsersList.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    setBatchProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const user of selectedUsersList) {
      try {
        let newTiers = [...(user.active_tiers || [])];
        
        if (batchTierAction === 'add') {
          if (!newTiers.includes(batchTierValue)) {
            newTiers.push(batchTierValue);
          }
        } else {
          newTiers = newTiers.filter(t => t !== batchTierValue);
        }
        
        await axios.put(`${API_URL}/api/admin/users/${user.id}`, {
          active_tiers: newTiers
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to update user ${user.email}:`, error);
      }
    }
    
    setBatchProcessing(false);
    setShowBatchTierDialog(false);
    setBatchTierValue('');
    
    if (successCount > 0) {
      toast.success(`Updated ${successCount} users`);
      fetchUsers();
    }
    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} users`);
    }
    
    clearSelection();
  };

  const handleBatchEmail = () => {
    const selectedUsersList = getSelectedUsersList();
    if (selectedUsersList.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    // Copy emails to clipboard
    const emails = selectedUsersList.map(u => u.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success(`${selectedUsersList.length} email addresses copied to clipboard`);
  };

  // Send Broadcast Email to selected users
  const handleSendBatchEmail = async () => {
    if (!batchEmailSubject.trim() || !batchEmailMessage.trim()) {
      toast.error('Please enter both subject and message');
      return;
    }
    
    const selectedUsersList = getSelectedUsersList();
    if (selectedUsersList.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    setSendingBatchEmail(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/broadcast/send`, {
        recipient_type: 'selected',
        selected_user_ids: selectedUsersList.map(u => u.id),
        subject: batchEmailSubject,
        message: batchEmailMessage,
        channel: 'email'
      });
      
      toast.success(`Email sent to ${response.data.recipients_count || response.data.total_recipients || selectedUsersList.length} users`);
      setShowBatchEmailDialog(false);
      setBatchEmailSubject('');
      setBatchEmailMessage('');
      clearSelection();
    } catch (error) {
      // FastAPI 422 returns detail as an array of objects; never pass that to toast.error
      const detail = error.response?.data?.detail;
      let msg = 'Failed to send emails';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
      }
      toast.error(msg);
    } finally {
      setSendingBatchEmail(false);
    }
  };

  // Batch Status Change (suspend/reactivate/delete)
  const handleBatchStatusChange = async () => {
    const selectedUsersList = getSelectedUsersList();
    if (selectedUsersList.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    setBatchProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const user of selectedUsersList) {
      try {
        if (batchStatusAction === 'delete') {
          await axios.delete(`${API_URL}/api/admin/users/${user.id}`);
        } else {
          await axios.put(`${API_URL}/api/admin/users/${user.id}`, {
            status: batchStatusAction === 'suspend' ? 'suspended' : 'active'
          });
        }
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to update user ${user.email}:`, error);
      }
    }
    
    setBatchProcessing(false);
    setShowBatchStatusDialog(false);
    
    if (successCount > 0) {
      const actionText = batchStatusAction === 'suspend' ? 'suspended' : 
                         batchStatusAction === 'reactivate' ? 'reactivated' : 'deleted';
      toast.success(`${successCount} users ${actionText}`);
      fetchUsers();
    }
    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} users`);
    }
    
    clearSelection();
  };

  const getActivityIcon = (iconName) => {
    const icons = {
      'shield': Shield,
      'shield-off': ShieldOff,
      'shield-check': ShieldCheck,
      'message-square': MessageSquare,
      'message-circle': MessageCircle,
      'message-square-plus': MessageSquarePlus,
      'book': BookOpen,
      'book-open': BookOpen,
      'calendar': Calendar,
      'calendar-x': CalendarX,
      'mail': Mail,
      'heart': Heart,
      'check-circle': CheckCircle2,
      'lock': Lock,
      'bell': Bell,
      'diamond': Diamond,
      'send': Send,
      'inbox': Inbox,
      'star': Star,
      'log-in': LogIn,
      'refresh-cw': RefreshCw,
      'clipboard-check': ClipboardCheck,
      'credit-card': CreditCard,
      'alert-circle': AlertCircle,
      'user-plus': UserPlus,
      'phone': Smartphone,
      'smartphone': Smartphone,
      'activity': Activity,
      'user': Users,
      'users': Users
    };
    return icons[iconName] || Activity;
  };

  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return null;
    if (profileImage.startsWith('http')) return profileImage;
    // Remove /api prefix if present since API_URL already includes it or handle correctly
    if (profileImage.startsWith('/api/')) {
      return `${API_URL}${profileImage.substring(4)}`; // Remove leading /api
    }
    return `${API_URL}${profileImage}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Sort users based on selected sort option
  const getSortedUsers = () => {
    // First apply date filter
    let filteredUsers = users;
    if (dateRange.from || dateRange.to) {
      filteredUsers = users.filter(user => {
        if (!user.created_at) return false;
        const joinDate = new Date(user.created_at);
        joinDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        if (dateRange.from && dateRange.to) {
          const fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          return joinDate >= fromDate && joinDate <= toDate;
        } else if (dateRange.from) {
          const fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          return joinDate >= fromDate;
        } else if (dateRange.to) {
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          return joinDate <= toDate;
        }
        return true;
      });
    }
    
    // Then apply sorting
    return [...filteredUsers].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name':
          return (a.full_name || '').localeCompare(b.full_name || '');
        case 'last_active':
          return new Date(b.last_login || 0) - new Date(a.last_login || 0);
        default:
          return 0;
      }
    });
  };

  const sortedUsers = getSortedUsers();

  const clearDateFilter = () => {
    setDateRange({ from: null, to: null });
  };

  const formatDateRange = () => {
    if (!dateRange.from && !dateRange.to) return 'Date Range';
    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    if (dateRange.from && dateRange.to) {
      return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
    } else if (dateRange.from) {
      return `From ${formatDate(dateRange.from)}`;
    } else {
      return `Until ${formatDate(dateRange.to)}`;
    }
  };

  const hasDateFilter = dateRange.from || dateRange.to;

  // Date preset helpers
  const applyDatePreset = (preset) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    let from = new Date();
    
    switch (preset) {
      case 'last7':
        from.setDate(today.getDate() - 7);
        break;
      case 'last30':
        from.setDate(today.getDate() - 30);
        break;
      case 'last90':
        from.setDate(today.getDate() - 90);
        break;
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setDateRange({ from, to: lastDayOfLastMonth });
        return;
      case 'thisYear':
        from = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }
    from.setHours(0, 0, 0, 0);
    setDateRange({ from, to: today });
  };

  return (
    <>
      <Card className="card-base">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Management 
              {hasDateFilter 
                ? <span className="text-base font-normal">({sortedUsers.length} of {users.length} users)</span>
                : <span className="text-base font-normal text-gray-500">({users.length} users)</span>
              }
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search users..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <Select value={tierFilter || "all"} onValueChange={(v) => setTierFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  {TIER_OPTIONS.map(tier => (
                    <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter-select">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Only (default)</SelectItem>
                  <SelectItem value="all">All (incl. cancelled)</SelectItem>
                  <SelectItem value="cancelled_nonpayment">Cancelled — Non-Payment</SelectItem>
                  <SelectItem value="grace">In Grace Period</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="deactivated">Deactivated</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40" data-testid="sort-by-select">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="last_active">Last Active</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Date Range Filter */}
              <Popover open={showDateFilter} onOpenChange={setShowDateFilter}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`w-auto min-w-[140px] justify-start text-left font-normal ${hasDateFilter ? 'border-forest text-forest' : ''}`}
                    data-testid="date-range-filter"
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    <span className="truncate">{formatDateRange()}</span>
                    {hasDateFilter && (
                      <X 
                        className="h-4 w-4 ml-2 hover:text-red-500" 
                        onClick={(e) => { e.stopPropagation(); clearDateFilter(); }}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-3 border-b">
                    <h4 className="font-medium text-sm text-forest">Filter by Join Date</h4>
                    <p className="text-xs text-gray-500">Select a date range or use quick presets</p>
                  </div>
                  
                  {/* Quick Presets */}
                  <div className="p-3 border-b bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 mb-2">Quick Select</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('last7')}
                      >
                        Last 7 days
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('last30')}
                      >
                        Last 30 days
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('last90')}
                      >
                        Last 90 days
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('thisMonth')}
                      >
                        This month
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('lastMonth')}
                      >
                        Last month
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={() => applyDatePreset('thisYear')}
                      >
                        This year
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 space-y-3">
                    <div>
                      <Label className="text-xs text-gray-600">From</Label>
                      <CalendarComponent
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                        className="rounded-md border"
                        initialFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">To</Label>
                      <CalendarComponent
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                        className="rounded-md border"
                        disabled={(date) => dateRange.from && date < dateRange.from}
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={clearDateFilter}
                      >
                        Clear
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1 btn-primary"
                        onClick={() => setShowDateFilter(false)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={handleSyncVipCircles} variant="outline" disabled={syncingVipCircles}>
                {syncingVipCircles ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Sync VIP Circles
              </Button>
              <Button 
                onClick={() => setShowBulkImportDialog(true)} 
                variant="outline"
                className="border-forest text-forest hover:bg-forest/10"
                data-testid="bulk-import-btn"
              >
                <Upload className="h-4 w-4 mr-2" /> Bulk Import
              </Button>
              <Button onClick={() => setShowCreateUserDialog(true)} className="btn-primary">
                <Plus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Batch Action Toolbar */}
          {selectedUsers.size > 0 && (
            <div className="mb-4 p-3 bg-forest/5 border border-forest/20 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedUsers.size === sortedUsers.length && sortedUsers.length > 0}
                  onCheckedChange={toggleSelectAll}
                  className="border-forest"
                />
                <span className="text-sm font-medium text-forest">
                  {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
                </span>
                <Button variant="ghost" size="sm" onClick={clearSelection} className="text-gray-500 h-7">
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBatchEmail}
                  className="h-8"
                  data-testid="batch-copy-emails"
                >
                  <Mail className="h-4 w-4 mr-1" /> Copy Emails
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowBatchEmailDialog(true)}
                  className="h-8"
                  data-testid="batch-send-email"
                >
                  <Send className="h-4 w-4 mr-1" /> Send Email
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowBatchTierDialog(true)}
                  className="h-8"
                  data-testid="batch-change-tier"
                >
                  <Diamond className="h-4 w-4 mr-1" /> Change Tier
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowBatchStatusDialog(true)}
                  className="h-8"
                  data-testid="batch-status"
                >
                  <Shield className="h-4 w-4 mr-1" /> Status
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBatchExport}
                  className="h-8"
                  data-testid="batch-export"
                >
                  <Download className="h-4 w-4 mr-1" /> Export
                </Button>
              </div>
            </div>
          )}

          {/* Select All Row (when no selection) */}
          {selectedUsers.size === 0 && sortedUsers.length > 0 && !loading && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <Checkbox
                checked={false}
                onCheckedChange={toggleSelectAll}
                className="border-gray-300"
              />
              <span>Select all {sortedUsers.length} users</span>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto"></div>
            </div>
          ) : sortedUsers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No users found.</p>
          ) : (
            <div className="space-y-4">
              {sortedUsers.map((u) => (
                <div key={u.id} className={`p-4 border rounded-xl ${selectedUsers.has(u.id) ? 'border-forest bg-forest/5' : u.status === 'suspended' ? 'border-red-300 bg-red-50' : u.status === 'deleted' ? 'border-gray-300 bg-gray-100' : 'border-sand'}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedUsers.has(u.id)}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                        className="border-forest"
                        data-testid={`select-user-${u.id}`}
                      />
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getProfileImageUrl(u.profile_image)} />
                        <AvatarFallback className={`${u.status === 'suspended' ? 'bg-red-500' : 'bg-forest'} text-white`}>
                          {u.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-forest">{u.full_name}</p>
                          {u.status === 'suspended' && <Badge className="bg-red-500 text-white text-xs">Suspended</Badge>}
                          {u.status === 'deleted' && <Badge className="bg-gray-500 text-white text-xs">Deleted</Badge>}
                          {u.role === 'admin' && <Badge className="bg-purple-600 text-white text-xs">Admin</Badge>}
                          {u.role === 'coach' && <Badge className="bg-blue-600 text-white text-xs">Coach</Badge>}
                          {u.is_retreat_vip && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs" data-testid={`vip-badge-${u.id}`}><Crown className="h-3 w-3 mr-1 inline" />VIP</Badge>}
                          {u.is_flagged && <Badge className="bg-orange-500 text-white text-xs">Flagged</Badge>}
                        </div>
                        <p className="text-sm text-gray-500">{u.email}</p>
                        {/* Contact Info Row */}
                        <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                          {u.phone_number && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {u.phone_number}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Joined {formatDate(u.created_at)}
                          </span>
                          {u.last_login && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Last login {formatDate(u.last_login)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {/* Active Tiers */}
                      <div className="flex flex-wrap gap-1">
                        {u.active_tiers?.map(tier => (
                          <Badge key={tier} variant="outline" className="text-xs">
                            {tier.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {(!u.active_tiers || u.active_tiers.length === 0) && (
                          <Badge variant="outline" className="text-xs text-gray-400">No tiers</Badge>
                        )}
                      </div>
                      {/* Last Purchase / Room Info */}
                      {(u.retreat_room_name || u.last_purchase_product) && (
                        <div className="text-xs text-gray-500 flex items-center gap-2" data-testid={`user-purchase-${u.id}`}>
                          {u.retreat_room_name && (
                            <Badge className="bg-teal-100 text-teal-700 border-teal-200 text-xs" data-testid={`room-badge-${u.id}`}>{u.retreat_room_name}</Badge>
                          )}
                          {!u.retreat_room_name && u.last_purchase_product && (
                            <>Purchased: <span className="font-medium text-forest">{u.last_purchase_product}</span></>
                          )}
                        </div>
                      )}
                      {/* Inner Circle Bonus Info */}
                      {u.inner_circle_bonus && (
                        <div className="text-xs flex items-center gap-2" data-testid={`bonus-info-${u.id}`}>
                          <Badge className={`text-xs ${u.inner_circle_bonus.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                            IC Bonus: {u.inner_circle_bonus.months}mo from {u.inner_circle_bonus.source_label}
                          </Badge>
                          <span className="text-gray-400">
                            expires {new Date(u.inner_circle_bonus.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fetchUserActivity(u.id)}
                          disabled={loadingActivity}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Activity
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedUserForAction(u);
                            setEditName(u.full_name || '');
                            setEditEmail(u.email || '');
                            setShowUserActionsDialog(true);
                          }}
                          data-testid={`open-user-actions-${u.id}`}
                        >
                          Actions
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
            <DialogDescription>Create a new user account with membership access</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={createUserForm.full_name}
                onChange={(e) => setCreateUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={createUserForm.phone_number || ''}
                onChange={(e) => setCreateUserForm(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="+1 555 123 4567"
              />
            </div>
            <div>
              <Label>Password (leave blank to auto-generate)</Label>
              <Input
                type="password"
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={createUserForm.role}
                onValueChange={(v) => setCreateUserForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Member</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Membership Tiers *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {TIER_OPTIONS.map(tier => (
                  <div key={tier.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={tier.value}
                      checked={createUserForm.active_tiers?.includes(tier.value)}
                      onCheckedChange={(checked) => {
                        setCreateUserForm(prev => ({
                          ...prev,
                          active_tiers: checked 
                            ? [...(prev.active_tiers || []), tier.value]
                            : (prev.active_tiers || []).filter(t => t !== tier.value)
                        }));
                      }}
                    />
                    <label htmlFor={tier.value} className="text-sm">{tier.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="send_welcome_email"
                checked={createUserForm.send_welcome_email}
                onCheckedChange={(checked) => setCreateUserForm(prev => ({ ...prev, send_welcome_email: checked }))}
              />
              <label htmlFor="send_welcome_email" className="text-sm">Send welcome email with login details</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleCreateUser}>Create Member</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Actions Dialog */}
      <Dialog open={showUserActionsDialog} onOpenChange={setShowUserActionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Actions: {selectedUserForAction?.full_name}</DialogTitle>
          </DialogHeader>
          {selectedUserForAction && (
            <div className="space-y-4">
              {/* Password Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Password</h4>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleResetPassword(selectedUserForAction.id)}
                  >
                    <Mail className="h-3 w-3 mr-1" /> Email Reset
                  </Button>
                  {selectedUserForAction.phone_number && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleSMSPasswordReset(selectedUserForAction.id)}
                    >
                      <Phone className="h-3 w-3 mr-1" /> SMS Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Email Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Resend Welcome Email</h4>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleResendWelcomeEmail(selectedUserForAction.id, 'general')}
                  >
                    General
                  </Button>
                  {selectedUserForAction.active_tiers?.includes('inner_circle') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleResendWelcomeEmail(selectedUserForAction.id, 'inner_circle')}
                    >
                      Inner Circle
                    </Button>
                  )}
                  {selectedUserForAction.active_tiers?.includes('inner_edge') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleResendWelcomeEmail(selectedUserForAction.id, 'inner_edge')}
                    >
                      Inner Edge
                    </Button>
                  )}
                </div>
              </div>

              {/* Sister Diamonds Collective — Payment Link */}
              <div
                className="space-y-2 p-3 rounded-lg"
                style={{ backgroundColor: '#FFF7F2', border: '1px solid rgba(192,108,84,0.25)' }}
              >
                <h4 className="text-xs uppercase tracking-wide font-medium" style={{ color: '#C06C54' }}>
                  Sister Diamonds Collective
                </h4>
                <p className="text-xs" style={{ color: '#1A3C34', opacity: 0.75 }}>
                  Send a private SDC payment page to this sister so she can choose her plan, accept program agreements, and finalize her seat.
                </p>
                <Button
                  size="sm"
                  onClick={() => handleSendSdcLink(selectedUserForAction)}
                  disabled={sendingSdcLink}
                  style={{ backgroundColor: '#C06C54', color: '#ffffff' }}
                  data-testid={`user-send-sdc-payment-link-${selectedUserForAction.id}`}
                >
                  {sendingSdcLink ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send SDC Payment Link
                </Button>
              </div>

              {/* Session Wallet */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Session Wallet</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-white p-2 rounded text-center">
                      <p className="text-gray-500">Coach</p>
                      <p className="font-bold text-forest text-lg">{selectedUserForAction.coach_sessions_remaining || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded text-center">
                      <p className="text-gray-500">Wonder Twin</p>
                      <p className="font-bold text-terracotta text-lg">{selectedUserForAction.founder_sessions_remaining || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded text-center">
                      <p className="text-gray-500">Inner Edge</p>
                      <p className="font-bold text-amber-700 text-lg">{selectedUserForAction.inner_edge_sessions_remaining || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded text-center">
                      <p className="text-gray-500">SDC Coach</p>
                      <p className="font-bold text-purple-700 text-lg">{selectedUserForAction.sdc_coach_sessions_remaining || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded text-center">
                      <p className="text-gray-500">SDC WT</p>
                      <p className="font-bold text-purple-600 text-lg">{selectedUserForAction.sdc_wonder_twin_sessions_remaining || 0}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Select value={walletType} onValueChange={setWalletType}>
                      <SelectTrigger className="flex-1 h-8 text-xs">
                        <SelectValue placeholder="Session type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="coach_sessions">Coaching</SelectItem>
                        <SelectItem value="founder_sessions">Wonder Twin</SelectItem>
                        <SelectItem value="inner_edge_sessions">Inner Edge</SelectItem>
                        <SelectItem value="sdc_coach_sessions">SDC Coach</SelectItem>
                        <SelectItem value="sdc_wonder_twin_sessions">SDC WT</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="+/-"
                      value={walletAmount || ''}
                      onChange={(e) => setWalletAmount(parseInt(e.target.value) || 0)}
                      className="w-20 h-8 text-xs"
                    />
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
                      onClick={handleWalletAdjust}
                      disabled={!walletType || !walletAmount}
                    >
                      Adjust
                    </Button>
                  </div>
                </div>
              </div>

              {/* Edit Profile */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Edit Profile</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="space-y-1">
                    <Label htmlFor="edit-user-name" className="text-xs text-gray-500">Full Name</Label>
                    <Input
                      id="edit-user-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="edit-user-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-user-email" className="text-xs text-gray-500">Email</Label>
                    <Input
                      id="edit-user-email"
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="edit-user-email-input"
                    />
                    <p className="text-[11px] text-amber-700 leading-snug">
                      Make sure the new email is already updated in Ontraport, or webhooks may create a duplicate account.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs btn-primary"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    data-testid="save-user-profile-btn"
                  >
                    {savingProfile ? 'Saving…' : 'Save Profile'}
                  </Button>
                </div>
              </div>

              {/* Safety Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Safety Actions</h4>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleAdminFlagUser(selectedUserForAction.id, selectedUserForAction.full_name)}
                  >
                    <Flag className="h-3 w-3 mr-1" /> Flag User
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleRestrictDMs(selectedUserForAction.id, !selectedUserForAction.dm_restricted)}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" /> 
                    {selectedUserForAction.dm_restricted ? 'Restore DMs' : 'Restrict DMs'}
                  </Button>
                </div>
              </div>

              {/* Account Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Account Status</h4>
                {selectedUserForAction.status === 'active' ? (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Reason for suspension..."
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                      className="text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-orange-600 border-orange-300"
                      onClick={() => handleSuspendUser(selectedUserForAction.id)}
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Suspend User
                    </Button>
                  </div>
                ) : selectedUserForAction.status === 'suspended' ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-green-600 border-green-300"
                    onClick={() => handleReactivateUser(selectedUserForAction.id)}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Reactivate User
                  </Button>
                ) : null}
              </div>

              {/* Delete Actions */}
              <div className="space-y-2 pt-2 border-t">
                <h4 className="text-sm font-medium text-red-600">Danger Zone</h4>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 border-red-300"
                    onClick={() => handleDeleteUser(selectedUserForAction.id, false)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Soft Delete
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteUser(selectedUserForAction.id, true)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Permanent Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle>
                {userActivity?.user?.full_name ? `Activity: ${userActivity.user.full_name}` : 'User Activity'}
              </DialogTitle>
              {userActivity?.user?.id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadChargebackPack(userActivity.user)}
                  data-testid="chargeback-pack-btn"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0"
                  title="Download a ZIP with everything needed to defend a Stripe dispute for this user"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Chargeback Pack
                </Button>
              )}
            </div>
          </DialogHeader>
          {loadingActivity ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-forest" />
            </div>
          ) : userActivity ? (
            <div className="space-y-6">
              {/* User Info */}
              {userActivity.user && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-forest/20 flex items-center justify-center text-forest font-semibold">
                    {userActivity.user.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-medium">{userActivity.user.full_name}</p>
                    <p className="text-sm text-gray-500">{userActivity.user.email}</p>
                  </div>
                </div>
              )}

              {/* Summary Stats - using stats from backend */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-forest/10 rounded-lg">
                  <div className="text-2xl font-bold text-forest">{userActivity.stats?.total_logins || 0}</div>
                  <div className="text-xs text-gray-600">Total Logins</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{userActivity.stats?.total_journal_entries || 0}</div>
                  <div className="text-xs text-gray-600">Journal Entries</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{userActivity.stats?.total_posts || 0}</div>
                  <div className="text-xs text-gray-600">Posts</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{userActivity.stats?.diamond_points || 0}</div>
                  <div className="text-xs text-gray-600">Diamond Points</div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">{userActivity.stats?.total_bookings || 0}</div>
                  <div className="text-xs text-gray-600">Bookings</div>
                </div>
                <div className="text-center p-3 bg-pink-50 rounded-lg">
                  <div className="text-xl font-bold text-pink-600">{userActivity.stats?.emails_sent || 0}</div>
                  <div className="text-xs text-gray-600">Emails Sent</div>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <div className="text-xl font-bold text-indigo-600">{userActivity.stats?.sms_sent || 0}</div>
                  <div className="text-xs text-gray-600">SMS Sent</div>
                </div>
              </div>

              {/* Recent Activity - using activities array from backend */}
              {userActivity.activities?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Activity ({userActivity.activities.length} items)</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {userActivity.activities.map((activity, idx) => {
                      const IconComponent = getActivityIcon(activity.icon);
                      return (
                        <div
                          key={idx}
                          data-testid={`activity-row-${idx}`}
                          className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg"
                        >
                          <IconComponent className="h-4 w-4 text-forest mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 break-words">{activity.description}</p>
                            {activity.preview && (
                              <p
                                data-testid={`activity-preview-${idx}`}
                                className="text-xs text-gray-600 italic mt-1 line-clamp-3 whitespace-pre-line"
                              >
                                "{activity.preview}"
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-gray-200 rounded-full">{activity.category}</span>
                              {activity.template_name && (
                                <span
                                  data-testid={`activity-template-${idx}`}
                                  className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full"
                                  title="Email template used"
                                >
                                  tpl: {activity.template_name}
                                </span>
                              )}
                              {activity.channel && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full uppercase">
                                  {activity.channel}
                                </span>
                              )}
                              {activity.notification_type && activity.category === 'system' && (
                                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                  {activity.notification_type}
                                </span>
                              )}
                              {activity.status && activity.status !== 'sent' && (
                                <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">
                                  {activity.status}
                                </span>
                              )}
                              {activity.link && (
                                <span className="text-xs text-blue-600 truncate max-w-[180px]" title={activity.link}>
                                  → {activity.link}
                                </span>
                              )}
                              <p className="text-xs text-gray-400">{formatDate(activity.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No activities message */}
              {(!userActivity.activities || userActivity.activities.length === 0) && (
                <p className="text-center text-gray-500 py-4">No recent activity recorded</p>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No activity data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={(open) => {
        setShowBulkImportDialog(open);
        if (!open) resetBulkImport();
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-forest" />
              Bulk Import Users
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple users at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Template Download */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-blue-800">Need a template?</p>
                <p className="text-xs text-blue-600">Download our CSV template with the correct format</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                <Download className="h-4 w-4 mr-1" /> Template
              </Button>
            </div>
            
            {/* File Upload */}
            {!bulkImportFile && !bulkImportResults && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-forest transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-400">CSV file with columns: email, name, phone (optional), tier (optional)</p>
                </label>
              </div>
            )}
            
            {/* Parsing indicator */}
            {bulkImportParsing && (
              <div className="text-center py-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-forest" />
                <p className="text-sm text-gray-500 mt-2">Parsing CSV file...</p>
              </div>
            )}
            
            {/* Preview Data */}
            {bulkImportData.length > 0 && !bulkImportResults && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-forest">
                      {bulkImportData.length} users ready to import
                    </p>
                    <p className="text-xs text-gray-500">From: {bulkImportFile?.name}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetBulkImport}>
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                </div>
                
                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Phone</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tier(s)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {bulkImportData.slice(0, 10).map((user, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                          <td className="px-3 py-2">{user.email}</td>
                          <td className="px-3 py-2">{user.full_name}</td>
                          <td className="px-3 py-2 text-gray-500">{user.phone_number || '-'}</td>
                          <td className="px-3 py-2">
                            {user.tiers?.length > 0 ? (
                              user.tiers.map((t, i) => (
                                <Badge key={i} variant="outline" className="mr-1 text-xs">{t}</Badge>
                              ))
                            ) : (
                              <span className="text-gray-400">inner_circle (default)</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bulkImportData.length > 10 && (
                    <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                      ... and {bulkImportData.length - 10} more users
                    </div>
                  )}
                </div>
                
                {/* Options */}
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="send-emails"
                    checked={bulkImportSendEmails}
                    onCheckedChange={(checked) => setBulkImportSendEmails(checked)}
                  />
                  <label htmlFor="send-emails" className="text-sm">
                    Send welcome emails with login credentials
                  </label>
                </div>
                
                {/* Import Progress */}
                {importing && (
                  <div className="space-y-2">
                    <Progress value={bulkImportProgress} className="h-2" />
                    <p className="text-xs text-gray-500 text-center">Importing users...</p>
                  </div>
                )}
                
                {/* Import Button */}
                <Button 
                  className="w-full btn-primary" 
                  onClick={handleBulkImport}
                  disabled={importing}
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import {bulkImportData.length} Users
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Results */}
            {bulkImportResults && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-green-600">{bulkImportResults.created}</p>
                    <p className="text-xs text-gray-600">Created</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-yellow-600">{bulkImportResults.skipped}</p>
                    <p className="text-xs text-gray-600">Skipped</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Users className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-600">{bulkImportResults.total}</p>
                    <p className="text-xs text-gray-600">Total</p>
                  </div>
                </div>
                
                {/* Errors List */}
                {bulkImportResults.errors?.length > 0 && (
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-3 py-2 border-b border-red-200">
                      <p className="text-sm font-medium text-red-800">
                        {bulkImportResults.errors.length} errors
                      </p>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {bulkImportResults.errors.map((err, idx) => (
                        <div key={idx} className="px-3 py-2 text-sm border-b border-red-100 last:border-0">
                          <span className="text-gray-500">Row {err.row}:</span>{' '}
                          <span className="font-medium">{err.email}</span>{' '}
                          <span className="text-red-600">- {err.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Created Users with Passwords */}
                {bulkImportResults.created_users?.length > 0 && (
                  <div className="border border-green-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-3 py-2 border-b border-green-200 flex items-center justify-between">
                      <p className="text-sm font-medium text-green-800">
                        Created Users & Passwords
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const data = bulkImportResults.created_users.map(u => 
                            `${u.email},${u.name},${u.temp_password}`
                          ).join('\n');
                          const blob = new Blob([`email,name,password\n${data}`], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'imported_users_passwords.csv';
                          a.click();
                        }}
                      >
                        <Download className="h-4 w-4 mr-1" /> Export
                      </Button>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {bulkImportResults.created_users.slice(0, 20).map((user, idx) => (
                        <div key={idx} className="px-3 py-2 text-sm border-b border-green-100 last:border-0 flex justify-between">
                          <span>{user.email}</span>
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{user.temp_password}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Done Button */}
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    setShowBulkImportDialog(false);
                    resetBulkImport();
                  }}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch Tier Change Dialog */}
      <Dialog open={showBatchTierDialog} onOpenChange={setShowBatchTierDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Diamond className="h-5 w-5 text-forest" />
              Change Tier for {selectedUsers.size} Users
            </DialogTitle>
            <DialogDescription>
              Add or remove a tier from all selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={batchTierAction} onValueChange={setBatchTierAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Tier</SelectItem>
                  <SelectItem value="remove">Remove Tier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={batchTierValue} onValueChange={setBatchTierValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tier" />
                </SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map(tier => (
                    <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              <p className="font-medium">This will {batchTierAction === 'add' ? 'add' : 'remove'} the selected tier {batchTierAction === 'add' ? 'to' : 'from'} {selectedUsers.size} users.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchTierDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleBatchTierChange}
              disabled={!batchTierValue || batchProcessing}
            >
              {batchProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Apply to {selectedUsers.size} Users</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Send Email Dialog */}
      <Dialog open={showBatchEmailDialog} onOpenChange={setShowBatchEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-forest" />
              Send Email to {selectedUsers.size} Users
            </DialogTitle>
            <DialogDescription>
              Compose and send an email to all selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Email subject..."
                value={batchEmailSubject}
                onChange={(e) => setBatchEmailSubject(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Write your message here..."
                value={batchEmailMessage}
                onChange={(e) => setBatchEmailMessage(e.target.value)}
                rows={6}
              />
              <p className="text-xs text-gray-500">
                You can use {'{name}'} to personalize with the recipient's name
              </p>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{selectedUsers.size}</strong> users will receive this email
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSendBatchEmail}
              disabled={!batchEmailSubject.trim() || !batchEmailMessage.trim() || sendingBatchEmail}
            >
              {sendingBatchEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {selectedUsers.size} Users
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Status Change Dialog */}
      <Dialog open={showBatchStatusDialog} onOpenChange={setShowBatchStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-forest" />
              Change Status for {selectedUsers.size} Users
            </DialogTitle>
            <DialogDescription>
              Update the account status for all selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={batchStatusAction} onValueChange={setBatchStatusAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="suspend">
                    <span className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      Suspend Accounts
                    </span>
                  </SelectItem>
                  <SelectItem value="reactivate">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Reactivate Accounts
                    </span>
                  </SelectItem>
                  <SelectItem value="delete">
                    <span className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-red-600" />
                      Delete Accounts
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className={`p-3 rounded-lg ${
              batchStatusAction === 'delete' ? 'bg-red-50' : 
              batchStatusAction === 'suspend' ? 'bg-yellow-50' : 'bg-green-50'
            }`}>
              <p className={`text-sm font-medium ${
                batchStatusAction === 'delete' ? 'text-red-800' : 
                batchStatusAction === 'suspend' ? 'text-yellow-800' : 'text-green-800'
              }`}>
                {batchStatusAction === 'suspend' && `This will suspend ${selectedUsers.size} user accounts. They will not be able to log in.`}
                {batchStatusAction === 'reactivate' && `This will reactivate ${selectedUsers.size} user accounts. They will be able to log in again.`}
                {batchStatusAction === 'delete' && `Warning: This will permanently delete ${selectedUsers.size} user accounts. This action cannot be undone.`}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchStatusDialog(false)}>
              Cancel
            </Button>
            <Button 
              className={batchStatusAction === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}
              onClick={handleBatchStatusChange}
              disabled={batchProcessing}
            >
              {batchProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {batchStatusAction === 'suspend' && 'Suspend'}
                  {batchStatusAction === 'reactivate' && 'Reactivate'}
                  {batchStatusAction === 'delete' && 'Delete'}
                  {' '}{selectedUsers.size} Users
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminUsersManagement;
